import React, { useCallback } from "react";
import "./BondCard.css";
import {Accordion, ListGroup} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faCircle,
    faWifi,
    faHouse,
    faEthernet,
    faBolt,
    faCode,
    faClock
} from "@fortawesome/free-solid-svg-icons";
import Form from "react-bootstrap/Form";
import Card from "react-bootstrap/Card";
import Badge from "react-bootstrap/Badge";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import InputGroup from "react-bootstrap/InputGroup";
import { useTimezone } from "../../../../contexts/TimezoneContext";
import ReactJson from "react-json-view";
import { truncateFirmwareVersion, formatTargetName } from "../../../../../../lib/ui/badgeUtils";
import BrandingProfileCard from "../BrandingProfileCard/BrandingProfileCard";

function BondCard({bondData, hideBridge = false}) {
    // Use timezone context for all date formatting - must be called before any conditional returns
    const { formatDate, getRelativeTime } = useTimezone();

    const decodeBase64Ssid = useCallback((base64Ssid) => {
        try {
            return atob(base64Ssid);
        } catch (e) {
            return base64Ssid;
        }
    }, []);

    const formatUptime = useCallback((seconds) => {
        const days = Math.floor(seconds / 86400);
        const hours = Math.floor((seconds % 86400) / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        return `${days}d ${hours}h ${minutes}m`;
    }, []);

    if (!bondData) {
        return null;
    }

    const sysVersion = bondData.sys?.version || {};
    const sysWifiSta = bondData.sys?.wifi?.sta || bondData.sys?.eth?.sta || {};
    const sysEth = bondData.sys?.eth || {};
    const sysPower = bondData.sys?.power || {};
    const sysTime = bondData.sys?.time || {};
    const bridge = bondData.bridge || {};
    const connectivity = bondData.connectivity || {};

    // Convert connectivity timestamp from milliseconds to ISO string
    const getConnectivityTimestamp = () => {
        if (connectivity.timestamp) {
            return new Date(connectivity.timestamp).toISOString();
        }
        return null;
    };

    return (
        <Card>
            <Card.Body className="pe-3 ps-3 pb-3">
                {/* Bond Information Section */}
                <div className="p-3">
                    {/* Connectivity Status */}
                    {/*{connectivity && connectivity.connected !== undefined && (*/}
                    {/*    <div className="mb-3 p-2 border rounded">*/}
                    {/*        <div className="d-flex align-items-center justify-content-between">*/}
                    {/*            <div>*/}
                    {/*                <FontAwesomeIcon*/}
                    {/*                    icon={faCircle}*/}
                    {/*                    className="me-2"*/}
                    {/*                    style={{color: connectivity.connected ? '#4caf7d' : '#e57373'}}*/}
                    {/*                />*/}
                    {/*                <Badge bg={connectivity.connected ? "success" : "danger"} className="me-2">*/}
                    {/*                    {connectivity.connected ? "Online" : "Offline"}*/}
                    {/*                </Badge>*/}
                    {/*            </div>*/}
                    {/*        </div>*/}
                    {/*    </div>*/}
                    {/*)}*/}

                    {connectivity?.timestamp && (
                        <div className="d-flex align-items-stretch gap-2 mb-2">
                            <FloatingLabel
                                label={connectivity.connected ? `Last Connected ${getRelativeTime(getConnectivityTimestamp())}` : `Disconnected ${getRelativeTime(getConnectivityTimestamp())}`}
                                className="flex-fill"
                            >
                                <Form.Control readOnly={true} value={formatDate(getConnectivityTimestamp())}/>
                            </FloatingLabel>
                            <Badge bg={connectivity.connected ? "success" : "danger"} style={{display: 'flex', alignItems: 'center', justifyContent: 'center', paddingLeft: '1rem', paddingRight: '1rem'}}>
                                {connectivity.connected ? "Online" : "Offline"}
                            </Badge>
                        </div>
                    )}

                    <div className="d-flex gap-2 mb-2">
                        <FloatingLabel label="Bond ID" className="flex-fill">
                            <Form.Control readOnly={true} value={bondData.id}/>
                        </FloatingLabel>
                        <FloatingLabel label="Token" className="flex-fill">
                            <Form.Control readOnly={true} value={bondData.token}/>
                        </FloatingLabel>
                    </div>

                    <div className="d-flex gap-2 mb-2">
                        {sysVersion.target && (
                            <FloatingLabel label="Target" className="flex-fill">
                                <Form.Control readOnly={true} value={formatTargetName(sysVersion.target)}/>
                            </FloatingLabel>
                        )}
                        {sysVersion.fw_ver && (
                            <FloatingLabel label="Firmware Version" className="flex-fill">
                                <Form.Control readOnly={true} value={truncateFirmwareVersion(sysVersion.fw_ver)} title={sysVersion.fw_ver}/>
                            </FloatingLabel>
                        )}
                    </div>

                    {sysVersion.make && (
                        <div className="d-flex gap-2 mb-2">
                            <FloatingLabel label="Make" className="flex-fill">
                                <Form.Control readOnly={true} value={sysVersion.make}/>
                            </FloatingLabel>
                            {sysVersion.model && (
                                <FloatingLabel label="Model" className="flex-fill">
                                    <Form.Control readOnly={true} value={sysVersion.model}/>
                                </FloatingLabel>
                            )}
                        </div>
                    )}

                    {sysVersion.uptime && (
                        <FloatingLabel label="Uptime" className="mb-2">
                            <Form.Control readOnly={true} value={formatUptime(sysVersion.uptime)}/>
                        </FloatingLabel>
                    )}

                    <div className="d-flex gap-2 mb-2">
                        <FloatingLabel label={`Created ${getRelativeTime(bondData.created)}`} className="flex-fill">
                            <Form.Control readOnly={true} value={formatDate(bondData.created)}/>
                        </FloatingLabel>
                        <FloatingLabel label={`Last Modified ${getRelativeTime(bondData.modified)}`} className="flex-fill">
                            <Form.Control readOnly={true} value={formatDate(bondData.modified)}/>
                        </FloatingLabel>
                    </div>
                </div>

                {/* Customization Profile */}
                <BrandingProfileCard bondId={bondData.id} />

                {/* Network Section */}
                {(sysWifiSta.ip || sysWifiSta.addr || sysEth.ip || sysEth.addr) && (
                    <Accordion defaultActiveKey="" className="mb-2">
                        <Accordion.Item eventKey="0">
                            <Accordion.Header>
                                <div className="justify-content-between w-100">
                                    <div className="float-start">
                                        <FontAwesomeIcon icon={faWifi} className="me-2"/>
                                        Network
                                    </div>
                                </div>
                            </Accordion.Header>
                            <Accordion.Body className="p-3">
                                {(sysWifiSta.ip || sysWifiSta.addr) && (
                                    <>
                                        <h6><FontAwesomeIcon icon={faWifi} className="me-2"/>WiFi</h6>
                                        <div className="d-flex gap-2 mb-2">
                                            <FloatingLabel label="IP Address" className="flex-fill">
                                                <Form.Control readOnly={true} value={sysWifiSta.ip || sysWifiSta.addr}/>
                                            </FloatingLabel>
                                            {sysWifiSta.mac && (
                                                <FloatingLabel label="MAC Address" className="flex-fill">
                                                    <Form.Control readOnly={true} value={sysWifiSta.mac}/>
                                                </FloatingLabel>
                                            )}
                                            {sysWifiSta.gw && (
                                                <FloatingLabel label="Gateway" className="flex-fill">
                                                    <Form.Control readOnly={true} value={sysWifiSta.gw}/>
                                                </FloatingLabel>
                                            )}
                                        </div>
                                        <div className="d-flex gap-2 mb-2">
                                            {sysWifiSta.ssid && (
                                                <FloatingLabel label="SSID" className="flex-fill">
                                                    <Form.Control readOnly={true} value={decodeBase64Ssid(sysWifiSta.ssid)}/>
                                                </FloatingLabel>
                                            )}
                                            {sysWifiSta.bssid && (
                                                <FloatingLabel label="BSSID" className="flex-fill">
                                                    <Form.Control readOnly={true} value={sysWifiSta.bssid}/>
                                                </FloatingLabel>
                                            )}
                                            {sysWifiSta.freq !== undefined && (
                                                <FloatingLabel label="Frequency" className="flex-fill">
                                                    <Form.Control readOnly={true} value={`${sysWifiSta.freq} GHz`}/>
                                                </FloatingLabel>
                                            )}
                                        </div>
                                        <div className="d-flex gap-2 mb-3">
                                            {sysWifiSta.dns && (
                                                <FloatingLabel label="DNS" className="flex-fill">
                                                    <Form.Control readOnly={true} value={sysWifiSta.dns}/>
                                                </FloatingLabel>
                                            )}
                                            {sysWifiSta.dns_alt && (
                                                <FloatingLabel label="DNS Alt" className="flex-fill">
                                                    <Form.Control readOnly={true} value={sysWifiSta.dns_alt}/>
                                                </FloatingLabel>
                                            )}
                                            {sysWifiSta.netmask && (
                                                <FloatingLabel label="Netmask" className="flex-fill">
                                                    <Form.Control readOnly={true} value={sysWifiSta.netmask}/>
                                                </FloatingLabel>
                                            )}
                                        </div>

                                        {/* Raw JSON */}
                                        <div className="mt-3">
                                            <Accordion>
                                                <Accordion.Item eventKey="json">
                                                    <Accordion.Header>
                                                        <FontAwesomeIcon icon={faCode} className="me-2" />
                                                        Raw JSON
                                                    </Accordion.Header>
                                                    <Accordion.Body>
                                                        <ReactJson
                                                            src={sysWifiSta}
                                                            theme="ocean"
                                                            collapsed={1}
                                                            displayDataTypes={false}
                                                            displayObjectSize={false}
                                                            enableClipboard={true}
                                                            name={false}
                                                        />
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            </Accordion>
                                        </div>
                                    </>
                                )}
                                {(sysEth.ip || sysEth.addr) && (
                                    <>
                                        <h6><FontAwesomeIcon icon={faEthernet} className="me-2 mt-3"/>Ethernet</h6>
                                        <div className="d-flex gap-2 mb-2">
                                            <FloatingLabel label="IP Address" className="flex-fill">
                                                <Form.Control readOnly={true} value={sysEth.ip || sysEth.addr}/>
                                            </FloatingLabel>
                                            {sysEth.mac && (
                                                <FloatingLabel label="MAC Address" className="flex-fill">
                                                    <Form.Control readOnly={true} value={sysEth.mac}/>
                                                </FloatingLabel>
                                            )}
                                        </div>

                                        {/* Raw JSON */}
                                        <div className="mt-3">
                                            <Accordion>
                                                <Accordion.Item eventKey="json-eth">
                                                    <Accordion.Header>
                                                        <FontAwesomeIcon icon={faCode} className="me-2" />
                                                        Raw JSON
                                                    </Accordion.Header>
                                                    <Accordion.Body>
                                                        <ReactJson
                                                            src={sysEth}
                                                            theme="ocean"
                                                            collapsed={1}
                                                            displayDataTypes={false}
                                                            displayObjectSize={false}
                                                            enableClipboard={true}
                                                            name={false}
                                                        />
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            </Accordion>
                                        </div>
                                    </>
                                )}
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                )}

                {/* Time Section */}
                {sysTime && Object.keys(sysTime).filter(key => !key.startsWith('_')).length > 0 && (
                    <Accordion defaultActiveKey="" className="mb-2">
                        <Accordion.Item eventKey="0">
                            <Accordion.Header>
                                <div className="justify-content-between w-100">
                                    <div className="float-start">
                                        <FontAwesomeIcon icon={faClock} className="me-2"/>
                                        Time
                                    </div>
                                </div>
                            </Accordion.Header>
                            <Accordion.Body className="p-3">
                                <div className="d-flex gap-2 mb-2">
                                    {sysTime.tz !== undefined && (
                                        <FloatingLabel label="Timezone" className="flex-fill">
                                            <Form.Control readOnly={true} value={sysTime.tz || 'NULL'}/>
                                        </FloatingLabel>
                                    )}
                                    {sysTime.grid && (
                                        <FloatingLabel label="Grid" className="flex-fill">
                                            <Form.Control readOnly={true} value={sysTime.grid}/>
                                        </FloatingLabel>
                                    )}
                                    {sysTime.time_set !== undefined && (
                                        <FloatingLabel label="Time Set" className="flex-fill">
                                            <Form.Control readOnly={true} value={sysTime.time_set ? 'Yes' : 'No'}/>
                                        </FloatingLabel>
                                    )}
                                </div>
                                {sysTime.unix_time !== undefined && (
                                    <FloatingLabel label="Unix Time" className="mb-2">
                                        <Form.Control readOnly={true} value={`${sysTime.unix_time} (${new Date(sysTime.unix_time * 1000).toISOString()})`}/>
                                    </FloatingLabel>
                                )}

                                {/* Raw JSON */}
                                <div className="mt-3">
                                    <Accordion>
                                        <Accordion.Item eventKey="json">
                                            <Accordion.Header>
                                                <FontAwesomeIcon icon={faCode} className="me-2" />
                                                Raw JSON
                                            </Accordion.Header>
                                            <Accordion.Body>
                                                <ReactJson
                                                    src={sysTime}
                                                    theme="ocean"
                                                    collapsed={1}
                                                    displayDataTypes={false}
                                                    displayObjectSize={false}
                                                    enableClipboard={true}
                                                    name={false}
                                                />
                                            </Accordion.Body>
                                        </Accordion.Item>
                                    </Accordion>
                                </div>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                )}

                {/* Bridge Section */}
                {!hideBridge && bridge.name && (
                    <Accordion defaultActiveKey="" className="mb-2">
                        <Accordion.Item eventKey="0">
                            <Accordion.Header>
                                <div className="justify-content-between w-100">
                                    <div className="float-start">
                                        <FontAwesomeIcon icon={faCircle} className="me-2"/>
                                        Bridge
                                    </div>
                                    <Badge bg="secondary" className="float-end">
                                        {bridge.name}
                                    </Badge>
                                </div>
                            </Accordion.Header>
                            <Accordion.Body className="p-3">
                                <div className="d-flex gap-2 mb-2">
                                    <FloatingLabel label="Name" className="flex-fill">
                                        <Form.Control readOnly={true} value={bridge.name}/>
                                    </FloatingLabel>
                                    {bridge.location && (
                                        <FloatingLabel label="Location" className="flex-fill">
                                            <Form.Control readOnly={true} value={bridge.location}/>
                                        </FloatingLabel>
                                    )}
                                    {bridge.bluelight !== undefined && (
                                        <FloatingLabel label="Bluelight" className="flex-fill">
                                            <Form.Control readOnly={true} value={bridge.bluelight}/>
                                        </FloatingLabel>
                                    )}
                                </div>

                                {/* Raw JSON */}
                                <div className="mt-3">
                                    <Accordion>
                                        <Accordion.Item eventKey="json">
                                            <Accordion.Header>
                                                <FontAwesomeIcon icon={faCode} className="me-2" />
                                                Raw JSON
                                            </Accordion.Header>
                                            <Accordion.Body>
                                                <ReactJson
                                                    src={bridge}
                                                    theme="ocean"
                                                    collapsed={1}
                                                    displayDataTypes={false}
                                                    displayObjectSize={false}
                                                    enableClipboard={true}
                                                    name={false}
                                                />
                                            </Accordion.Body>
                                        </Accordion.Item>
                                    </Accordion>
                                </div>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                )}

                {/* Power Information */}
                {sysPower.battery_level !== undefined && (
                    <div className="mt-3 p-3 bg-light rounded">
                        <h6><FontAwesomeIcon icon={faBolt} className="me-2"/>Power</h6>
                        <FloatingLabel label="Battery Level" className="mb-2">
                            <Form.Control readOnly={true} value={`${sysPower.battery_level}%`}/>
                        </FloatingLabel>
                    </div>
                )}

                {/* Raw JSON Viewer */}
                <div className="mt-3">
                    <Accordion>
                        <Accordion.Item eventKey="json">
                            <Accordion.Header>
                                <FontAwesomeIcon icon={faCode} className="me-2" />
                                Raw JSON
                            </Accordion.Header>
                            <Accordion.Body>
                                <ReactJson
                                    src={bondData}
                                    theme="ocean"
                                    collapsed={1}
                                    displayDataTypes={false}
                                    displayObjectSize={false}
                                    enableClipboard={true}
                                    name={false}
                                />
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                </div>
            </Card.Body>
        </Card>
    );
}

export default BondCard;
